import { useCallback, useEffect, useState } from "react"

interface UseAsync<T> {
  operation: () => Promise<T>
  onComplete?: (result: T) => void
  onError?: (e: Error) => void
  disabled?: boolean
}

interface AsyncState<T> {
  data: T | undefined
  error: Error | undefined
  isLoading: boolean
  isCalled: boolean
}

const DEFAULT_STATE = {
  isLoading: false,
  data: undefined,
  error: undefined,
  isCalled: false,
}

export function useAsync<T>({
  operation,
  disabled,
  onComplete,
  onError,
}: UseAsync<T>) {
  const [state, setState] = useState<AsyncState<T>>(DEFAULT_STATE)
  const { data, error } = state

  const call = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, isCalled: true }))
      const result = await operation()
      setState((prev) => ({
        ...prev,
        isLoading: false,
        data: result,
        error: undefined,
      }))
    } catch (e) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: e as Error,
        data: undefined,
      }))
    }
  }, [operation])

  useEffect(() => {
    if (disabled) {
      return
    }

    call()
  }, [call, disabled])

  useEffect(() => {
    if (data && onComplete) {
      onComplete(data)
    }
  }, [data, onComplete])

  useEffect(() => {
    if (error && onError) {
      onError(error)
    }
  }, [error, onError])

  return {
    ...state,
    call,
  }
}
